<script setup lang="ts">
import { computed, ref } from 'vue';

import { AppSvgIcon, AppFoldableTransition } from '@libs/ui';

import type { TripSummaryHeader } from './TripOverviewHeaderMobile.helper';

interface TripOverviewHeaderSummaryButtonProps {
  numberOfPax: number;
  calendarText: string;
  numberOfNights: number;
  headerSegments: TripSummaryHeader[];
  slotHeight: number;
}
const props = defineProps<TripOverviewHeaderSummaryButtonProps>();

const areButtonsVisible = ref<boolean>(false);

const chevronIcon = computed<'chevronDown' | 'chevronUp'>(() =>
  areButtonsVisible.value ? 'chevronUp' : 'chevronDown'
);

function getKey(segment: TripSummaryHeader, index: number): string {
  return `${segment.text ? segment.text : 'no-text'} + ${
    segment.icon ? segment.icon : 'no-icon'
  } ${index}`;
}
</script>

<template>
  <div
    :class="[
      'trip-overview-header-summary-button',
      { 'are-buttons-visible': areButtonsVisible },
    ]"
  >
    <button
      class="summary-button"
      @click="areButtonsVisible = !areButtonsVisible"
    >
      <div class="summary-button-wrapper">
        <div class="label">
          <span class="title">
            <div
              v-for="(segment, index) in props.headerSegments"
              :key="getKey(segment, index)"
              class="segment"
            >
              <AppSvgIcon
                v-if="segment.icon"
                class="icon"
                :name="segment.icon"
                size="16"
                color="color-soft-black"
              />
              <span v-if="segment.text" class="segment-text">
                {{ segment.text }}
              </span>
            </div>
          </span>
          <span class="description">
            {{ props.numberOfPax }}
            {{
              $t(
                'planner.pax.travelers',
                props.numberOfPax
              ).toLocaleLowerCase()
            }}, {{ props.calendarText }},
            {{ props.numberOfNights }}
            {{ $t('planner.nights', props.numberOfNights).toLowerCase() }}
          </span>
        </div>
        <AppSvgIcon :name="chevronIcon" size="16" color="color-soft-black" />
      </div>
    </button>

    <AppFoldableTransition :height="props.slotHeight">
      <div v-if="areButtonsVisible">
        <slot />
      </div>
    </AppFoldableTransition>
  </div>
</template>

<style scoped lang="scss">
.trip-overview-header-summary-button {
  margin: 0 var(--spacing-16) var(--spacing-16);
  padding: var(--spacing-16) var(--spacing-12) var(--spacing-16);
  border-radius: var(--border-radius-8);
  box-shadow: var(--shadow-elevation-20);
  border: 1px solid var(--color-neutral-30);

  @include for-less-or-equal-than-599-px {
    margin: 0 var(--spacing-12) var(--spacing-16) var(--spacing-12);
  }

  &.are-buttons-visible {
    padding: var(--spacing-16) var(--spacing-12) var(--spacing-12);
  }

  .summary-button {
    all: unset;
    width: 100%;

    .summary-button-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: var(--spacing-4);

      .label {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: calc(100% - var(--spacing-16) - var(--spacing-4));

        .title {
          @include font-label-m-bold;

          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: var(--spacing-4);
          max-width: 100%;

          .segment {
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: 100%;
            overflow: hidden;

            &:first-child {
              flex-shrink: 0;
            }

            .icon {
              transform: translateY(-1px);
              flex-shrink: 0;
            }

            .segment-text {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }
        }

        .description {
          @include font-label-s-light;
        }
      }
    }
  }
}
</style>
